/**
 * Change Log — the cross-repo engineering changelog shown in System Admin
 * ▸ Change Log (`/admin/changelog`).
 *
 * Source of truth is a committed, typed array in the dashboard server
 * (`apps/server/src/data/changelog.ts`). Every time work is merged to
 * `main` in ANY of the active stack repos, an entry is appended there in
 * the same chunk of work (see the umbrella `CLAUDE.md` "Change Log" rule
 * and `docs/changelog.md`). The server reads it; the web tab renders it as
 * a month calendar.
 *
 * This module is the shared CONTRACT: the entry shape, the enumerations,
 * and the pure date-grouping helpers reused by both the server route and
 * the web calendar — so "which days have updates" is computed identically
 * on both sides.
 */

/** The active repos in the post-redesign topology (umbrella
 * `ARCHITECTURE.md`). The change log spans exactly these six.
 *
 * `xop-resellers` (XOP Reseller, the reseller portal on reseller-edge) was
 * added 2026-08-28. It is a SIXTH deploy unit, not a rename of an existing
 * one — the umbrella CLAUDE.md's "five active repos" wording predates it. */
export const CHANGELOG_REPOS = [
  "xop-agents",
  "xop-brain",
  "xop-bug-snap",
  "xop-edge-customer",
  "xop-edge-staff",
  "xop-resellers",
] as const;
export type ChangelogRepo = (typeof CHANGELOG_REPOS)[number];

/** Conventional-commit-flavored change classes. Each maps to one OKLCH
 * theme token on the web side (no raw hex) — see the web panel's
 * `TYPE_META`. Listed in display / legend order. */
export const CHANGELOG_TYPES = [
  "feat",
  "fix",
  "build",
  "docs",
  "refactor",
  "perf",
  "chore",
  "security",
] as const;
export type ChangelogType = (typeof CHANGELOG_TYPES)[number];

/** Conventional-commit prefixes → change type (incl. common aliases). */
const COMMIT_TYPE_ALIASES: Record<string, ChangelogType> = {
  feat: "feat",
  feature: "feat",
  fix: "fix",
  bugfix: "fix",
  build: "build",
  ci: "build",
  deps: "build",
  docs: "docs",
  doc: "docs",
  refactor: "refactor",
  perf: "perf",
  chore: "chore",
  test: "chore",
  tests: "chore",
  style: "chore",
  revert: "chore",
  security: "security",
  sec: "security",
};

/**
 * Hard ceiling (in characters) for an auto-derived change-log title. Mirrors
 * the `MAX_TITLE_LEN` invariant pinned by the server's `changelog.test.ts`:
 * an entry title is a single scannable headline, never a pasted paragraph.
 * Exported so the test, the canonical parser, and the build-free
 * `scripts/sync-changelog.mjs` mirror all clamp to the *same* number.
 */
export const CHANGELOG_TITLE_MAX_LEN = 160;

/**
 * Clamp a title to {@link CHANGELOG_TITLE_MAX_LEN} so an over-long commit
 * subject can never break the title-only invariant when it is auto-synced
 * into the change log (the recurring "deploy red: title is essay-length"
 * failure). Cuts on a nearby word boundary when there is one and appends a
 * single ellipsis; the result is always ≤ `max`. Short titles pass through
 * untouched. Pure — kept in sync with the inline `truncateTitle` in
 * `scripts/sync-changelog.mjs`.
 */
export function truncateChangelogTitle(
  title: string,
  max = CHANGELOG_TITLE_MAX_LEN,
): string {
  const t = title.trim();
  if (t.length <= max) return t;
  const budget = max - 1; // reserve one char for the ellipsis
  const hard = t.slice(0, budget);
  const lastSpace = hard.lastIndexOf(" ");
  // Prefer a word boundary, but only when it doesn't lop off too much tail.
  const base =
    lastSpace >= budget - 24 && lastSpace > 0 ? hard.slice(0, lastSpace) : hard;
  return `${base.replace(/[\s.,;:!?—–-]+$/u, "")}…`;
}

/**
 * Parse a commit subject into a `{ type, title }` for an auto-derived
 * change-log entry. Recognises `type(scope)!: description` conventional
 * commits (incl. aliases like ci→build, test→chore); anything else falls
 * back to `chore` + the whole subject. The title is always clamped to
 * {@link CHANGELOG_TITLE_MAX_LEN} so a long commit subject can't break the
 * title-only invariant downstream. Pure — the canonical reference for
 * `scripts/sync-changelog.mjs` (which mirrors this to stay build-free).
 */
export function parseConventionalSubject(subject: string): {
  type: ChangelogType;
  title: string;
} {
  const m = /^([a-zA-Z]+)(?:\([^)]*\))?(?:!)?:\s*(.+)$/.exec(subject.trim());
  if (m) {
    const type = COMMIT_TYPE_ALIASES[m[1]!.toLowerCase()];
    if (type) return { type, title: truncateChangelogTitle(m[2]!.trim()) };
  }
  return { type: "chore", title: truncateChangelogTitle(subject.trim()) };
}

export interface ChangelogEntry {
  /** Calendar day the change landed on `main`, `YYYY-MM-DD` (local, no
   * timezone — it is a date, not an instant). */
  date: string;
  repo: ChangelogRepo;
  type: ChangelogType;
  /** One-line headline — the whole entry. The Change Log is a glanceable
   * calendar of *what* shipped; the *why* lives in the commit body / PR,
   * so there is intentionally no longer-form `summary` field. */
  title: string;
  /** Optional short commit SHA. */
  commit?: string;
  /** Optional PR / MR reference (e.g. "#42" or a URL). */
  pr?: string;
}

/** One calendar day's worth of entries, plus the per-type rollup the
 * calendar paints as markers. */
export interface ChangelogDay {
  /** `YYYY-MM-DD`. */
  date: string;
  count: number;
  /** Distinct change types present that day, in `CHANGELOG_TYPES` order. */
  types: ChangelogType[];
  entries: ChangelogEntry[];
}

/** Echo of a resolved "what's new since" window (the deep-link mode). */
export interface ChangelogSince {
  /** The desktop version asked about, if the query used `sinceVersion`. */
  version: string | null;
  /** The `YYYY-MM-DD` the window starts strictly after, or null if the
   * version couldn't be pinpointed in the release registry. */
  date: string | null;
}

/** The calendar response: the window's entries + the day rollup + the repo
 * set available to filter. */
export interface ChangelogResponse {
  entries: ChangelogEntry[];
  days: ChangelogDay[];
  /** Distinct repos present in the window, in `CHANGELOG_REPOS` order. */
  repos: ChangelogRepo[];
  /** Present only in "what's new since" mode — echoes the resolved window. */
  since?: ChangelogSince;
  generated_at: string;
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** True for a well-formed `YYYY-MM-DD` that is also a real calendar date
 * (rejects `2026-02-31`, `2026-13-01`, etc.). */
export function isValidYmd(value: string): boolean {
  if (!YMD.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** True for a well-formed `YYYY-MM`. */
export function isValidYearMonth(value: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(value)) return false;
  const m = Number(value.slice(5, 7));
  return m >= 1 && m <= 12;
}

/** The `YYYY-MM` a `YYYY-MM-DD` belongs to. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/**
 * Group entries by calendar day, **newest day first**, with per-type
 * rollups. Pure + deterministic so the server and the web compute the same
 * markers from the same data. Malformed dates are skipped defensively —
 * one bad seed row can never break the calendar.
 */
export function groupByDay(entries: readonly ChangelogEntry[]): ChangelogDay[] {
  const byDate = new Map<string, ChangelogEntry[]>();
  for (const e of entries) {
    if (!isValidYmd(e.date)) continue;
    const list = byDate.get(e.date);
    if (list) list.push(e);
    else byDate.set(e.date, [e]);
  }
  const days: ChangelogDay[] = [];
  for (const [date, list] of byDate) {
    const present = new Set(list.map((e) => e.type));
    const types = CHANGELOG_TYPES.filter((t) => present.has(t));
    days.push({ date, count: list.length, types, entries: list });
  }
  // Reverse-chronological by the `YYYY-MM-DD` string (lexicographic == date).
  days.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return days;
}

/** Distinct repos present across `entries`, in `CHANGELOG_REPOS` order. */
export function reposIn(entries: readonly ChangelogEntry[]): ChangelogRepo[] {
  const present = new Set(entries.map((e) => e.repo));
  return CHANGELOG_REPOS.filter((r) => present.has(r));
}

/**
 * Entries shipped **strictly after** `sinceDate` (`YYYY-MM-DD`) — i.e.
 * everything newer than the day a given version was released. Powers the
 * "What's new since version X" view (X's release date is resolved from the
 * desktop-release registry server-side). Lexicographic compare is correct
 * for zero-padded ISO dates. Malformed entry dates are skipped; an invalid
 * `sinceDate` means "can't pinpoint" → returns all entries (the caller
 * surfaces that as unresolved).
 */
export function entriesSince(
  entries: readonly ChangelogEntry[],
  sinceDate: string,
): ChangelogEntry[] {
  if (!isValidYmd(sinceDate)) return [...entries];
  return entries.filter((e) => isValidYmd(e.date) && e.date > sinceDate);
}
